import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import Sidebar from "./components/Sidebar";
import Navbar from "./components/Navbar";

const baseUrl = process.env.REACT_APP_API_BASE_URL;

const GamesPage = () => {
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editingIndex, setEditingIndex] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    labelName: "",
    formLabelName: "",
    price: "",
    image: null,
  });
  const [formErrors, setFormErrors] = useState({});

  const token = localStorage.getItem("token");

  useEffect(() => {
    fetchGames();
  }, []);

  // ✅ Fetch games
  const fetchGames = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${baseUrl}/game/list`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (result.status && result.data) {
        setGames(result.data);
      } else {
        throw new Error(result.message || "Failed to fetch games");
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // ✅ Handle form input
  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "image") {
      setFormData({ ...formData, image: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
    setFormErrors({ ...formErrors, [name]: "" }); // clear error
  };

  // ✅ Validation
  const validateForm = (isEdit = false) => {
    let errors = {};

    if (!formData.name || formData.name.trim().length < 3) {
      errors.name = "Game name must be at least 3 characters";
    }
    if (!formData.category) {
      errors.category = "Category is required";
    }
    if (!formData.labelName) {
      errors.labelName = "Label Name is required";
    }
    if (!formData.formLabelName) {
      errors.formLabelName = "Form Label Name is required";
    }
    if (!formData.price || isNaN(formData.price) || Number(formData.price) <= 0) {
      errors.price = "Price must be a positive number";
    }
    if (!isEdit && !formData.image) {
      errors.image = "Image is required for new game";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ✅ Normalize game response
  const normalizeGame = (res) => {
    return res?.data?.game || res?.data || res?.game || res;
  };

  // ✅ Add new game
  const handleAddNew = async () => {
    if (!validateForm(false)) return;

    try {
      const form = new FormData();
      form.append("name", formData.name);
      form.append("category", formData.category);
      form.append("labelName", formData.labelName);
      form.append("formLabelName", formData.formLabelName);
      form.append("price", formData.price);
      if (formData.image) form.append("image", formData.image);

      const res = await fetch(`${baseUrl}/game/create`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });

      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Add failed");

      const newGame = normalizeGame(result);
    //   if (!newGame || !newGame.name) throw new Error("Invalid game response");

      setGames((prev) => [...prev, newGame]);
      setFormData({ name: "", category: "", labelName: "", price: "", image: null });
      setShowModal(false);
      setEditingIndex(null);

    //   alert("Game created successfully!");
    toast.success("Game created successfully!");
        await fetchGames();
    } catch (error) {
     // alert(error.message);
      toast.error("Game created failed!");
    }
  };

  // ✅ Edit click
  const handleEdit = (index) => {
    setEditingIndex(index);
    setFormData({
      name: games[index].name,
      category: games[index].category,
      labelName: games[index].labelName,
      formLabelName: games[index].formLabelName,
      price: games[index].price,
      image: null,
    });
    setShowModal(true);
  };

  // ✅ Save edited game
  const handleSave = async () => {
  if (!validateForm(true)) return;

  const current = games[editingIndex];
  const gameId = current._id || current.id;

  try {
    const form = new FormData();
    form.append("name", formData.name);
    form.append("category", formData.category);
    form.append("labelName", formData.labelName);
    form.append("formLabelName", formData.formLabelName);
    form.append("price", formData.price);
    if (formData.image) form.append("image", formData.image);

    const res = await fetch(`${baseUrl}/game/edit/${gameId}`, {
      method: "PUT",
      headers: { Authorization: `Bearer ${token}` },
      body: form,
    });

    const result = await res.json();
    if (!res.ok) throw new Error(result.message || "Update failed");

    // ✅ Either update in place or re-fetch
    await fetchGames(); // <-- safest way (always up-to-date from server)

    setFormData({ name: "", category: "", labelName: "", price: "", image: null });
    setShowModal(false);
    setEditingIndex(null);

    // alert("Game updated successfully!");
    toast.success("Game updated successfully!");
  } catch (error) {
    // alert(error.message);
    toast.error("Game updated failed!");
  }
};

  // ✅ Delete
  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this game?")) return;
    try {
      const res = await fetch(`${baseUrl}/game/delete/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Delete failed");

      setGames((prev) => prev.filter((g) => g.id !== id && g._id !== id));
      toast.success("Game deleted successfully!");
    } catch (error) {
      toast.error("Game deleted failed!");
    }
  };

  // ✅ Toggle active/inactive
  const toggleActive = async (id, isActive) => {
    try {
      const endpoint = isActive
        ? `${baseUrl}/game/deactivate/${id}`
        : `${baseUrl}/game/activate/${id}`;
      const res = await fetch(endpoint, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` },
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.message || "Update failed");

      setGames((prev) =>
        prev.map((g) =>
          (g.id === id || g._id === id) ? { ...g, isActive: !isActive } : g
        )
      );
    } catch (error) {
      toast.error("Game activation/deactivation failed!");
    }
  };

  return (
    <div className="flex bg-gray-900 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col">
        <Navbar />
        <div className="p-6">
          <h2 className="text-3xl font-bold mb-6 text-white">Games Management</h2>

          {/* Add button */}
          <button
            onClick={() => {
              setEditingIndex(null);
              setFormData({ name: "", category: "", labelName: "", price: "", image: null });
              setShowModal(true);
            }}
            className="mb-6 bg-yellow text-black font-semibold px-4 py-2 rounded hover:bg-yellow-500"
          >
            Add Game
          </button>

          {/* Modal */}
          {showModal && (
            <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
              <div className="bg-gray-800 p-6 rounded shadow-lg w-1/2">
                <h3 className="text-xl font-semibold mb-4 text-white">
                  {editingIndex !== null ? "Edit Game" : "Add New Game"}
                </h3>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Game Name"
                      value={formData.name}
                      onChange={handleChange}
                      className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                    />
                    {formErrors.name && <p className="text-red-500 text-sm">{formErrors.name}</p>}
                  </div>
                  <div>
                    <input
                      type="text"
                      name="category"
                      placeholder="Category"
                      value={formData.category}
                      onChange={handleChange}
                      className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                    />
                    {formErrors.category && <p className="text-red-500 text-sm">{formErrors.category}</p>}
                  </div>
                  <div>
                    <input
                      type="text"
                      name="labelName"
                      placeholder="Label Name"
                      value={formData.labelName}
                      onChange={handleChange}
                      className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                    />
                    {formErrors.labelName && <p className="text-red-500 text-sm">{formErrors.labelName}</p>}
                  </div>
                  <div>
                    <input
                      type="number"
                      name="price"
                      placeholder="Price"
                      value={formData.price}
                      onChange={handleChange}
                      className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                    />
                    {formErrors.price && <p className="text-red-500 text-sm">{formErrors.price}</p>}
                  </div>
                  <div>
                    <input
                      type="text"
                      name="formLabelName"
                      placeholder="Form Label Name (Enter Paypal ID, Enter Game ID)"
                      value={formData.formLabelName}
                      onChange={handleChange}
                      className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                    />
                    {formErrors.formLabelName && <p className="text-red-500 text-sm">{formErrors.formLabelName}</p>}
                  </div>
                  <div>
                    <input
                      type="file"
                      name="image"
                      accept="image/*"
                      onChange={handleChange}
                      className="border border-gray-600 p-2 w-full mb-2 bg-gray-700 text-white"
                    />
                    {formErrors.image && <p className="text-red-500 text-sm">{formErrors.image}</p>}
                  </div>
                </div>

                <div className="mt-4 flex justify-end space-x-3">
                  <button
                    onClick={() => setShowModal(false)}
                    className="bg-gray-600 text-white px-4 py-2 rounded"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={editingIndex !== null ? handleSave : handleAddNew}
                    className="bg-yellow text-black px-4 py-2 rounded"
                  >
                    {editingIndex !== null ? "Save Changes" : "Add Game"}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Table */}
          {loading && <p className="text-gray-400">Loading games...</p>}
          {error && <p className="text-red-500">{error}</p>}
          {!loading && games.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-gray-800 rounded shadow text-left">
                <thead className="bg-yellow">
                  <tr>
                    <th className="py-2 px-4">Name</th>
                    <th className="py-2 px-4">Category</th>
                    <th className="py-2 px-4">Label Name</th>
                    <th className="py-2 px-4">Form Label Name</th>
                    <th className="py-2 px-4">Price</th>
                    <th className="py-2 px-4">Image</th>
                    <th className="py-2 px-4">Status</th>
                    <th className="py-2 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {games.map((item, index) => (
                    <tr key={item.id || item._id} className="border-b border-gray-900 hover:bg-gray-700 text-gray-300">
                      <td className="py-2 px-4">{item.name}</td>
                      <td className="py-2 px-4">{item.category}</td>
                      <td className="py-2 px-4">{item.labelName}</td>
                      <td className="py-2 px-4">{item.formLabelName}</td>
                      <td className="py-2 px-4">{item.price}</td>
                      <td className="py-2 px-4">
                        {item.image ? (
                          <img
                            src={
                              item.image.startsWith("http")
                                ? item.image
                                : `${baseUrl.replace("/admin", "")}/${item.image}`
                            }
                            alt={item.name}
                            className="h-12 w-12 object-cover rounded"
                          />
                        ) : (
                          <span className="text-gray-400 italic">No Image</span>
                        )}
                      </td>
                      <td className="py-2 px-4">
                        <span
                          className={`px-2 py-1 rounded text-sm ${
                            item.isActive ? "bg-green-200 text-green-700" : "bg-red-200 text-red-700"
                          }`}
                        >
                          {item.isActive ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="py-2 px-4 space-x-2">
                        <button
                          className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                          onClick={() => handleEdit(index)}
                        >
                          Edit
                        </button>
                        <button
                          className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                          onClick={() => handleDelete(item.id || item._id)}
                        >
                          Delete
                        </button>
                        <button
                          className={`px-3 py-1 rounded text-white ${
                            item.isActive ? "bg-gray-600 hover:bg-gray-700" : "bg-blue-600 hover:bg-blue-700"
                          }`}
                          onClick={() => toggleActive(item.id || item._id, item.isActive)}
                        >
                          {item.isActive ? "Deactivate" : "Activate"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GamesPage;
